import { useCallback, useEffect, useRef, useState } from "react";
import { api, streamUrl, posterUrl } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import SuggestedCreators from "@/components/SuggestedCreators";
import GuideLearnShelf from "@/components/GuideLearnShelf";
import { Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import WhatsAppEarningsDialog from "@/components/WhatsAppEarningsDialog";

// Windowing constants — tuned for weak mobile browsers.
// PAGE_SIZE: batch size on initial + pagination.
// RENDER_WINDOW: full VideoCards mounted around active. Others render as a
//   lightweight placeholder (poster image only) that preserves scroll-snap
//   geometry and never shows pure black.
// NEAR_WINDOW: cards allowed to attach a real <video> element. Everything
//   outside this window is a static poster image only.
// LOAD_MORE_AHEAD: when activeIdx is within this many cards of the end,
//   we fetch the next page.
const PAGE_SIZE = 8;
const RENDER_WINDOW = 3;
const NEAR_WINDOW = 1;
const LOAD_MORE_AHEAD = 3;

export default function Feed() {
  const { user, setUser } = useAuth();
  const [waOpen, setWaOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    let flag = false, seen = false;
    try {
      flag = localStorage.getItem("wa_new_user_prompt") === "1";
      seen = sessionStorage.getItem("wa_prompt_seen") === "1";
    } catch { /* ignore */ }
    const needs = user && !(user.whatsapp_number && user.whatsapp_boost_alerts && !user.whatsapp_opted_out);
    if (flag && needs && !seen) setWaOpen(true);
  }, [user]);

  const closeWa = () => {
    try {
      localStorage.removeItem("wa_new_user_prompt");
      sessionStorage.setItem("wa_prompt_seen", "1");
    } catch { /* ignore */ }
    setWaOpen(false);
  };
  const containerRef = useRef(null);
  // Pull-to-refresh state
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullStartYRef = useRef(null);
  // True only when the initial feed fetch itself failed — distinct from a
  // genuinely empty account, so we don't show "upload the first video" to
  // someone whose request just errored out.
  const [loadError, setLoadError] = useState(false);

  // In-place video updater — used by VideoCard whenever an action needs to
  // reflect on the card (like count, gift count, render_status transition).
  // Previously this refetched the entire feed via `loadInitial`, which
  // reshuffled the list by algorithm re-scoring and snapped the user's
  // scroll to a DIFFERENT video. Now:
  //   - If we get an updated video doc (with .id), patch it in place
  //   - If we get nothing, do nothing — the card's local state already
  //     handled the visual update. A full feed reload here would cost
  //     the user their scroll position AND their currently-playing video.
  const patchVideo = useCallback((updated) => {
    if (!updated || !updated.id) return;
    if (updated._remove) {
      // Explicit removal signal (e.g. "not interested"). Filter the video
      // out entirely so the feed continues from the next one.
      setVideos((prev) => prev.filter((v) => v.id !== updated.id));
      return;
    }
    setVideos((prev) => prev.map((v) => (v.id === updated.id ? { ...v, ...updated } : v)));
  }, []);

  // Initial load — small page so the phone doesn't try to prep 30 videos.
  const loadInitial = useCallback(async () => {
    try {
      const r = await api.get("/videos/feed", { params: { limit: PAGE_SIZE } });
      const data = Array.isArray(r.data) ? r.data : [];
      setVideos(data);
      setHasMore(data.length === PAGE_SIZE);
      setLoadError(false);
    } catch (e) {
      console.warn("Feed initial load failed", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pull the next page. Guarded so we don't fire concurrently.
  const loadMoreRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadMoreRef.current || !hasMore) return;
    loadMoreRef.current = true;
    setLoadingMore(true);
    try {
      const r = await api.get("/videos/feed", {
        params: { limit: PAGE_SIZE, skip: videos.length },
      });
      const next = Array.isArray(r.data) ? r.data : [];
      if (next.length === 0) {
        setHasMore(false);
      } else {
        setVideos((prev) => {
          // De-dupe on id in case the ranker re-sorts and returns overlap.
          const seen = new Set(prev.map((v) => v.id));
          const merged = [...prev];
          for (const v of next) {
            if (!seen.has(v.id)) merged.push(v);
          }
          return merged;
        });
        if (next.length < PAGE_SIZE) setHasMore(false);
      }
    } catch (e) {
      // Silent — user can still scroll among loaded videos.
      console.warn("Feed loadMore failed", e);
    } finally {
      loadMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [videos.length, hasMore]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Track which card is active based on scroll position.
  // Depends on `loading` (in addition to `activeIdx`) because the scrollable
  // container only exists once the loading/error/empty branches below give
  // way to the real feed markup — without it, this effect attaches its
  // listener while containerRef.current is still null (the initial "loading"
  // render), bails out via the `!el` guard, and then never gets a second
  // chance to attach once the container actually mounts, since activeIdx
  // never changes on its own. Net effect: the listener silently never
  // attaches and activeIdx stays stuck at 0 forever, no matter how far the
  // user scrolls.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollTop / el.clientHeight);
      if (idx !== activeIdx) setActiveIdx(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeIdx, loading]);

  // Fire pagination when the user is close to the end of loaded videos.
  useEffect(() => {
    if (!videos.length) return;
    if (activeIdx >= videos.length - LOAD_MORE_AHEAD) {
      loadMore();
    }
  }, [activeIdx, videos.length, loadMore]);

  // Pull-to-refresh — only when at the very top of the feed. Tracks
  // touch delta and shows the neon "// sync" indicator. Threshold to
  // trigger a real refresh is PULL_THRESHOLD_PX. Anything shorter snaps
  // back with no fetch, so it never fights normal scroll behavior.
  const PULL_THRESHOLD_PX = 80;
  const PULL_MAX_PX = 140;
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await api.get("/videos/feed", {
        params: { limit: PAGE_SIZE, _t: Date.now() },
      });
      const data = Array.isArray(r.data) ? r.data : [];
      setVideos(data);
      setHasMore(data.length === PAGE_SIZE);
      setActiveIdx(0);
      const el = containerRef.current;
      if (el) el.scrollTo({ top: 0, behavior: "instant" in el ? "instant" : "auto" });
    } catch (e) {
      // Silent — the existing videos still work.
      console.warn("Feed refresh failed", e);
    } finally {
      setRefreshing(false);
      setPullPx(0);
    }
  }, [refreshing]);

  // Tapping the FEED button in the bottom nav while already ON the feed
  // dispatches `feed:refresh` (see BottomNav.jsx). Refetch to give the
  // user a fresh algorithm shuffle — new videos, new order.
  useEffect(() => {
    const onExternalRefresh = () => {
      handleRefresh();
      // Neon-pink "// SHUFFLED" toast confirms the reshuffle happened.
      // Only fires on the event-driven path — pull-to-refresh has its
      // own "// sync" indicator so we don't want a double confirmation.
      toast("// SHUFFLED", {
        duration: 1000,
        style: {
          background: "#0a0a0a",
          border: "1px solid #FF007F",
          color: "#FF007F",
          fontFamily: "monospace",
          fontWeight: "700",
          letterSpacing: "0.08em",
          boxShadow: "0 0 12px rgba(255,0,127,0.55)",
        },
      });
    };
    window.addEventListener("feed:refresh", onExternalRefresh);
    return () => window.removeEventListener("feed:refresh", onExternalRefresh);
  }, [handleRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      if (el.scrollTop <= 0 && !refreshing) {
        pullStartYRef.current = e.touches[0].clientY;
      } else {
        pullStartYRef.current = null;
      }
    };
    const onTouchMove = (e) => {
      if (pullStartYRef.current === null) return;
      const dy = e.touches[0].clientY - pullStartYRef.current;
      if (dy <= 0) {
        setPullPx(0);
        return;
      }
      // Damped pull so it feels rubber-bandy after threshold
      const damped = dy < PULL_THRESHOLD_PX ? dy : PULL_THRESHOLD_PX + (dy - PULL_THRESHOLD_PX) * 0.35;
      setPullPx(Math.min(damped, PULL_MAX_PX));
    };
    const onTouchEnd = () => {
      if (pullStartYRef.current === null) return;
      pullStartYRef.current = null;
      if (pullPx >= PULL_THRESHOLD_PX) {
        handleRefresh();
      } else {
        setPullPx(0);
      }
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pullPx, refreshing, handleRefresh]);

  // Prefetch — narrower now. Only warm the next TWO videos' cache. Sending
  // an open-ended Range triggers the backend's progressive-proxy path
  // which streams AND tees to disk. We consume ~1 MB then abort to save
  // client bandwidth. All abort() and cleanup calls are try/catch'd so
  // a rare browser quirk (some Chromium builds throw during rapid
  // controller.abort()) can never blow up the whole feed via the
  // ErrorBoundary.
  const prefetchAbortRef = useRef([]);
  const _safeAbortAll = () => {
    try {
      for (const c of prefetchAbortRef.current || []) {
        try { c.abort(); } catch { /* browser quirk — ignore */ }
      }
    } catch { /* ignore */ }
    prefetchAbortRef.current = [];
  };
  useEffect(() => {
    _safeAbortAll();

    if (!videos.length) return;
    // Only prefetch REAL video files. Embeds (YouTube/Vimeo iframes) have
    // no storage_path so the stream endpoint 404s — hammering it here
    // just burns a request and can leak a hung reader in some Chromium
    // builds. Photo posts also have no video track. Skip both.
    const nextVideos = [
      videos[activeIdx + 1],
      videos[activeIdx + 2],
    ].filter((v) => v && !v.is_embed && !v.is_photo_post);
    if (nextVideos.length === 0) return;

    nextVideos.forEach((nextVideo) => {
      const nextId = nextVideo.id;
      const ctrl = new AbortController();
      prefetchAbortRef.current.push(ctrl);
      // Fully-async IIFE so every promise (including reader.cancel()
      // which is async) lands inside our try/catch. Prior version used
      // .then/.catch chains that let `reader.cancel()`'s rejection
      // escape as an unhandled promise rejection — React 19's dev
      // overlay catches those before our global suppressor and blows
      // up the whole feed with "signal is aborted without reason".
      (async () => {
        try {
          const r = await fetch(streamUrl(nextId), {
            method: "GET",
            headers: { Range: "bytes=0-" },
            signal: ctrl.signal,
            credentials: "include",
          });
          if (ctrl.signal.aborted || !r.body) return;
          const reader = r.body.getReader();
          let bytesRead = 0;
          const LIMIT = 1024 * 1024;
          try {
            while (bytesRead < LIMIT && !ctrl.signal.aborted) {
              const { done, value } = await reader.read();
              if (done) break;
              bytesRead += value.byteLength;
            }
          } finally {
            try { await reader.cancel(); } catch { /* already dead — ignore */ }
          }
        } catch { /* aborted / offline — swallow */ }
      })();
    });

    return _safeAbortAll;
  }, [activeIdx, videos]);

  if (loading) {
    return (
      <div
        data-testid="feed-loading"
        className="h-screen flex items-center justify-center bg-black"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 border-2 border-[#39FF14] border-t-transparent animate-spin" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
            buffering the void
          </p>
        </div>
      </div>
    );
  }

  if (loadError && videos.length === 0) {
    return (
      <div
        data-testid="feed-error"
        className="h-screen flex flex-col items-center justify-center bg-black px-6 text-center"
      >
        <Sparkles className="h-12 w-12 text-red-400 mb-6" strokeWidth={1.5} />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-red-400 mb-3">
          // connection.lost
        </p>
        <h2 className="font-display font-black text-3xl mb-3">
          couldn&apos;t load the feed
        </h2>
        <p className="text-white/60 max-w-sm mb-8">
          Something interrupted the request. Your videos are fine — try
          again.
        </p>
        <button
          onClick={loadInitial}
          data-testid="feed-error-retry-btn"
          className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)]"
        >
          Retry
        </button>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div
        data-testid="feed-empty"
        className="min-h-screen flex flex-col items-center justify-start bg-black px-6 pt-16 pb-24 text-center md:ml-60"
      >
        <Sparkles className="h-12 w-12 text-[#39FF14] mb-6" strokeWidth={1.5} />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#39FF14] mb-3">
          // empty.feed
        </p>
        <h2 className="font-display font-black text-3xl mb-3">
          no signal detected
        </h2>
        <p className="text-white/60 max-w-sm mb-8">
          The feed is waiting for its first transmission. Follow a creator or
          upload something to get it flowing.
        </p>
        <Link
          to="/upload"
          data-testid="feed-empty-upload-btn"
          className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] mb-12"
        >
          Upload the first video
        </Link>
        <div className="w-full max-w-3xl text-left">
          <GuideLearnShelf heading="New here? Learn MatrixShopping" className="mb-12" />
          <SuggestedCreators limit={12} />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="feed-container"
      className="h-screen w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide bg-black relative"
    >
      <WhatsAppEarningsDialog open={waOpen} onClose={closeWa} user={user} onSaved={setUser} />
      {/* Pull-to-refresh indicator — fades in as user pulls at top */}
      {(pullPx > 0 || refreshing) && (
        <div
          data-testid="pull-refresh-indicator"
          className="absolute top-0 left-0 right-0 z-50 flex items-center justify-center pointer-events-none"
          style={{
            height: `${refreshing ? 56 : pullPx}px`,
            opacity: Math.min(1, (refreshing ? 80 : pullPx) / 60),
          }}
        >
          <div className="flex flex-col items-center gap-1">
            <div
              className={`h-6 w-6 border-2 border-[#39FF14] border-t-transparent ${
                refreshing || pullPx >= 80 ? "animate-spin" : ""
              }`}
              style={{
                transform: refreshing ? "none" : `rotate(${pullPx * 3}deg)`,
                transition: refreshing ? "none" : "transform 40ms linear",
              }}
            />
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#39FF14]/80">
              {refreshing ? "// syncing" : pullPx >= 80 ? "// release" : "// sync"}
            </p>
          </div>
        </div>
      )}
      {videos.map((v, idx) => {
        const distance = Math.abs(idx - activeIdx);
        // Outside the render window → lightweight placeholder with the
        // poster thumbnail so scroll-past never shows pure black. This is
        // the fix for the "some videos are black" symptom during fast
        // scrolls where React hasn't caught up promoting the card to a
        // full VideoCard yet.
        if (distance > RENDER_WINDOW) {
          return (
            <div
              key={v.id}
              data-testid={`video-placeholder-${v.id}`}
              className="h-screen w-full snap-start bg-black relative overflow-hidden"
            >
              <img
                src={posterUrl(v.id)}
                alt=""
                loading="lazy"
                decoding="async"
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain opacity-90"
              />
            </div>
          );
        }
        return (
          <VideoCard
            key={v.id}
            video={v}
            active={idx === activeIdx}
            nearActive={distance <= NEAR_WINDOW}
            onUpdate={patchVideo}
            muted={muted}
            setMuted={setMuted}
          />
        );
      })}
      {loadingMore && (
        <div
          data-testid="feed-loading-more"
          className="h-16 flex items-center justify-center"
        >
          <div className="h-5 w-5 border-2 border-[#39FF14] border-t-transparent animate-spin" />
        </div>
      )}
      {/* End-of-feed marker — snap-target sized so the user can scroll INTO
          it and see they've reached the end (instead of feeling "stuck"). */}
      {!hasMore && !loadingMore && videos.length > 0 && (
        <div
          data-testid="feed-end"
          className="h-screen w-full snap-start bg-black flex flex-col items-center justify-center gap-3 px-6 text-center"
        >
          <div className="h-1 w-16 bg-[#39FF14] shadow-[0_0_12px_rgba(57,255,20,0.6)]" />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
            // end.of.transmission
          </p>
          <h2 className="font-display font-black text-2xl text-white">
            you&apos;re all caught up
          </h2>
          <p className="text-white/50 text-sm max-w-xs">
            Pull down at the top to refresh the feed, or head back home for
            fresh signals.
          </p>
          <div className="w-full max-w-md mt-6 text-left">
            <GuideLearnShelf heading="Learn MatrixShopping" limit={6} />
          </div>
          <button
            onClick={() => {
              const el = containerRef.current;
              if (el) el.scrollTo({ top: 0, behavior: "smooth" });
              setActiveIdx(0);
            }}
            data-testid="feed-end-back-top"
            className="mt-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:shadow-[0_0_16px_rgba(57,255,20,0.55)] transition-shadow"
          >
            back to top
          </button>
        </div>
      )}
    </div>
  );
}
